import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import { CloseIcon, SearchIcon } from "../../icons/glyphs";
import { useFieldControl } from "../Field/FieldContext";
import { IconButton } from "../IconButton";
import { usePopoverDismiss } from "../foundations/popoverDismiss";
import type { InputSize } from "../Input/foundations/inputTypes";
import {
  Accessories,
  Control,
  EMPTY_VALUE_SET,
  EmptyText,
  Input,
  Menu,
  Option,
  OptionDescription,
  OptionLabel,
  OptionMain,
  Shell,
  defaultMaxVisibleOptions,
  filterOptions,
  moveActiveIndex,
  type SearchOption,
} from "./searchComboboxShared";

export type { SearchOption } from "./searchComboboxShared";
export { SearchInputAccessory } from "./searchComboboxShared";

export interface SearchInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "size" | "disabled"
> {
  /** 검색어(부모 소유). 일반 텍스트 입력처럼 동작 — 서버 질의·debounce는 호출자 책임. */
  value: string;
  onValueChange: (value: string) => void;
  /** 자동완성 후보. 미지정 또는 빈 배열이면 메뉴 없는 plain 검색 필드로 동작한다. */
  options?: readonly SearchOption[];
  /** 검색 실행 콜백. 자동완성 항목 선택 시 그 label로, Enter 시 현재 입력값으로 호출된다. */
  onSearch?: (query: string) => void;
  /** "sm" | "md". 기본 "md". c6 InputSize와 통일. */
  inputSize?: InputSize;
  /** 컨트롤과 펼친 옵션 메뉴의 폭. 숫자는 px, 문자열은 CSS 길이. 미지정 시 부모 폭 100%.
   *  메뉴는 컨트롤 폭을 그대로 따른다(둘이 항상 같은 폭). */
  width?: number | string;
  invalid?: boolean;
  disabled?: boolean;
  /** 매칭 후보가 없을 때 문구. `renderOnEmpty` 가 없을 때만 쓰인다. */
  emptyMessage?: string;
  /** 매칭 후보가 0개일 때 메뉴에 렌더할 커스텀 노드. 현재 입력어를 인자로 받는다. */
  renderOnEmpty?: (query: string) => ReactNode;
  /** 메뉴에 한 번에 보이는 후보 상한. 기본 8. */
  maxVisibleOptions?: number;
  /** 입력 안쪽 오른쪽 끝의 액세서리(예: 단위 텍스트 `SearchInputAccessory`). clear 버튼 왼쪽에 놓인다. */
  accessory?: ReactNode;
  /** 입력값이 있을 때 안쪽 오른쪽 끝에 내부 관리 clear 버튼을 노출(IconButton + CloseIcon). */
  showClear?: boolean;
  /** 매칭 키워드 커스터마이즈. 기본은 label/value/description. */
  getOptionKeywords?: (option: SearchOption) => readonly string[];
}

const defaultEmptyMessage = "No matching options.";
const emptyOptions: readonly SearchOption[] = [];

/**
 * 텍스트 자동완성 검색 입력(콤보박스). 자유 텍스트로 후보를 필터하고, 항목을 고르면 입력란이 그
 * label로 채워진다(칩 없음 — 일반 검색창처럼). 선택/Enter 시 `onSearch`로 검색을 트리거한다.
 * 검색어 상태는 소유하지 않고 `onValueChange`로 위임 — 서버 질의·debounce는 호출자 책임.
 * 칩(토큰) 다중선택이 필요하면 `TokenSearchInput`을 쓴다. (RFC-filter-search-primitives)
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onValueChange,
    options,
    onSearch,
    inputSize = "md",
    width,
    invalid = false,
    disabled = false,
    emptyMessage = defaultEmptyMessage,
    renderOnEmpty,
    maxVisibleOptions = defaultMaxVisibleOptions,
    accessory,
    showClear = false,
    getOptionKeywords,
    id,
    className,
    placeholder,
    autoComplete = "off",
    autoCorrect = "off",
    autoCapitalize = "off",
    spellCheck = false,
    type = "search",
    onFocus,
    onBlur,
    onKeyDown,
    ...inputProps
  },
  forwardedRef,
) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wantsAutocompleteOpenRef = useRef(false);
  const fallbackInputId = useId();
  const listboxId = useId();
  const { controlId, disabled: isDisabled } = useFieldControl({
    componentName: "SearchInput",
    id,
    disabled,
  });
  const resolvedInputId = controlId ?? fallbackInputId;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const autocompleteOptions = options ?? emptyOptions;
  const hasAutocomplete = autocompleteOptions.length > 0;

  const setInputRef = useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef],
  );

  const visibleOptions = useMemo(
    () =>
      filterOptions({
        options: autocompleteOptions,
        excludeValues: EMPTY_VALUE_SET,
        query: value,
        maxVisibleOptions,
        getOptionKeywords,
      }),
    [autocompleteOptions, getOptionKeywords, maxVisibleOptions, value],
  );

  useEffect(() => {
    if (isDisabled || !hasAutocomplete) {
      setOpen(false);
    }
  }, [hasAutocomplete, isDisabled]);

  useEffect(() => {
    if (
      hasAutocomplete &&
      wantsAutocompleteOpenRef.current &&
      document.activeElement === inputRef.current
    ) {
      setOpen(true);
    }
  }, [hasAutocomplete, visibleOptions.length]);

  useEffect(() => {
    if (!open) {
      setActiveIndex(-1);
      return;
    }
    if (activeIndex >= visibleOptions.length) {
      setActiveIndex(-1);
    }
  }, [activeIndex, open, visibleOptions.length]);

  // Escape는 입력 onKeyDown에서 따로 처리하므로 hook의 Escape는 끈다.
  const dismissRefs = useMemo(() => [rootRef], []);
  const closeMenu = useCallback(() => setOpen(false), []);
  const dismissMenu = useCallback(() => {
    wantsAutocompleteOpenRef.current = false;
    closeMenu();
  }, [closeMenu]);
  usePopoverDismiss({ open, refs: dismissRefs, onClose: dismissMenu, closeOnEscape: false });

  // 자동완성 항목 선택 — 입력란을 label로 채우고(칩 없음) onSearch로 검색 트리거. 포커스는 유지.
  const selectOption = useCallback(
    (option: SearchOption) => {
      if (isDisabled || option.disabled) {
        return;
      }
      onValueChange(option.label);
      onSearch?.(option.label);
      wantsAutocompleteOpenRef.current = false;
      setOpen(false);
      setActiveIndex(-1);
    },
    [isDisabled, onSearch, onValueChange],
  );

  const hasValue = value !== "";
  const hasSearchAction = onSearch !== undefined;
  const handleClear = useCallback(() => {
    onValueChange("");
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [onValueChange]);
  const handleSearchAction = useCallback(() => {
    // 검색 뒤 다른 입력으로 옮기는 것은 소비처가 정한다. 기본 포커스를 먼저 주어
    // onSearch 가 옮긴 포커스를 뒤늦게 가져오지 않는다.
    inputRef.current?.focus();
    wantsAutocompleteOpenRef.current = false;
    setOpen(false);
    setActiveIndex(-1);
    onSearch?.(value);
  }, [onSearch, value]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onValueChange(event.target.value);
      wantsAutocompleteOpenRef.current = true;
      setOpen(hasAutocomplete);
      setActiveIndex(-1);
    },
    [hasAutocomplete, onValueChange],
  );

  const handleInputFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onFocus?.(event);
      if (!event.defaultPrevented) {
        wantsAutocompleteOpenRef.current = true;
        setOpen(hasAutocomplete);
      }
    },
    [hasAutocomplete, onFocus],
  );

  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      onBlur?.(event);
    },
    [onBlur],
  );

  const handleInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) {
        return;
      }

      if (event.key === "ArrowDown" && hasAutocomplete) {
        event.preventDefault();
        wantsAutocompleteOpenRef.current = true;
        setOpen(true);
        setActiveIndex((current) => moveActiveIndex(current, visibleOptions, 1));
        return;
      }

      if (event.key === "ArrowUp" && hasAutocomplete) {
        event.preventDefault();
        wantsAutocompleteOpenRef.current = true;
        setOpen(true);
        setActiveIndex((current) => moveActiveIndex(current, visibleOptions, -1));
        return;
      }

      if (event.key === "Escape") {
        wantsAutocompleteOpenRef.current = false;
        setOpen(false);
        setActiveIndex(-1);
        return;
      }

      if (event.key !== "Enter") {
        return;
      }
      if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
        return;
      }

      const activeOption = visibleOptions[activeIndex];
      if (hasAutocomplete && open) {
        event.preventDefault();
        if (activeOption !== undefined && !activeOption.disabled) {
          // 활성 자동완성 항목이 있으면 그것으로 선택 + 검색.
          selectOption(activeOption);
          return;
        }
        setOpen(false);
        setActiveIndex(-1);
        wantsAutocompleteOpenRef.current = false;
        onSearch?.(value);
        return;
      }

      if (onSearch === undefined) {
        return;
      }

      // 활성 항목이 없으면 현재 입력값으로 검색 실행.
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
      wantsAutocompleteOpenRef.current = false;
      onSearch(value);
    },
    [activeIndex, hasAutocomplete, onKeyDown, onSearch, open, selectOption, value, visibleOptions],
  );
  return (
    <Shell ref={rootRef} className={className} $width={width}>
      <Control $inputSize={inputSize} $invalid={invalid} $disabled={isDisabled}>
        <Input
          ref={setInputRef}
          id={resolvedInputId}
          value={value}
          disabled={isDisabled}
          aria-invalid={invalid || undefined}
          aria-autocomplete={hasAutocomplete ? "list" : undefined}
          aria-expanded={hasAutocomplete ? open : undefined}
          aria-controls={hasAutocomplete && open ? listboxId : undefined}
          role={hasAutocomplete ? "combobox" : undefined}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoCorrect={autoCorrect}
          autoCapitalize={autoCapitalize}
          spellCheck={spellCheck}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          {...inputProps}
        />

        {accessory || (showClear && hasValue) || hasSearchAction ? (
          <Accessories>
            {accessory}
            {showClear && hasValue ? (
              <IconButton
                aria-label="Clear"
                size="xs"
                variant="ghost"
                disabled={isDisabled}
                onClick={handleClear}
              >
                <CloseIcon width="0.72em" height="0.72em" />
              </IconButton>
            ) : null}
            {hasSearchAction ? (
              <IconButton
                aria-label="Search"
                size="xs"
                variant="ghost"
                disabled={isDisabled}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleSearchAction}
              >
                <SearchIcon width="0.9em" height="0.9em" />
              </IconButton>
            ) : null}
          </Accessories>
        ) : null}

        {open && hasAutocomplete && !isDisabled ? (
          <Menu id={listboxId} role="listbox">
            {visibleOptions.length === 0 ? (
              renderOnEmpty ? (
                renderOnEmpty(value)
              ) : (
                <EmptyText>{emptyMessage}</EmptyText>
              )
            ) : (
              visibleOptions.map((option, index) => (
                <Option
                  key={option.value}
                  type="button"
                  $active={index === activeIndex}
                  role="option"
                  disabled={option.disabled}
                  aria-selected={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectOption(option)}
                >
                  <OptionMain>
                    <OptionLabel $accentColor={option.accentColor}>{option.label}</OptionLabel>
                    {option.description ? (
                      <OptionDescription>{option.description}</OptionDescription>
                    ) : null}
                  </OptionMain>
                </Option>
              ))
            )}
          </Menu>
        ) : null}
      </Control>
    </Shell>
  );
});

SearchInput.displayName = "SearchInput";
